import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, Shield, Users, BookOpen, CheckCircle,
  Leaf, Wheat, Milk, Flame, Fish, Heart,
  Apple, Sparkles, LogIn,
} from 'lucide-react';

const DIETARY_SHORTCUTS = [
  { label: 'Gluten-Free',  icon: Wheat  },
  { label: 'Dairy-Free',   icon: Milk   },
  { label: 'Vegan',        icon: Leaf   },
  { label: 'Keto',         icon: Flame  },
  { label: 'Pescatarian',  icon: Fish   },
  { label: 'Halal',        icon: Heart  },
];

const FEATURES = [
  { icon: Shield,      label: 'Verified Products',  desc: 'Every product human-reviewed before it appears in the catalog' },
  { icon: BookOpen,    label: 'Recipe Discovery',   desc: 'Community recipes filtered to your exact dietary needs' },
  { icon: Users,       label: 'Care Profiles',      desc: 'Manage restrictions for everyone in your household' },
  { icon: CheckCircle, label: 'Smart Filtering',    desc: 'Filter any combination of dietary restrictions instantly' },
];

// Animated floating orb
const Orb = ({ style }) => (
  <div
    className="absolute rounded-full pointer-events-none"
    style={{
      background: 'radial-gradient(circle at 40% 40%, rgba(45,80,22,0.18), rgba(45,80,22,0.04) 70%)',
      filter: 'blur(40px)',
      ...style,
    }}
  />
);

const ComingSoonView = () => {
  const navigate                  = useNavigate();
  const [email, setEmail]         = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError]         = useState('');
  const [loading, setLoading]     = useState(false);
  const [scrollY, setScrollY]     = useState(0);
  const canvasRef                 = useRef(null);
  const animRef                   = useRef(null);

  // Parallax on scroll
  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Particle canvas — subtle floating dots
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let W = canvas.width  = canvas.offsetWidth;
    let H = canvas.height = canvas.offsetHeight;

    const resize = () => {
      W = canvas.width  = canvas.offsetWidth;
      H = canvas.height = canvas.offsetHeight;
    };
    window.addEventListener('resize', resize);

    const count = 38;
    const particles = Array.from({ length: count }, () => ({
      x:    Math.random() * W,
      y:    Math.random() * H,
      r:    1 + Math.random() * 2.5,
      dx:   (Math.random() - 0.5) * 0.35,
      dy:  -(0.15 + Math.random() * 0.3),
      o:    0.08 + Math.random() * 0.18,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      particles.forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(45,80,22,${p.o})`;
        ctx.fill();
        p.x += p.dx;
        p.y += p.dy;
        if (p.y < -10) { p.y = H + 10; p.x = Math.random() * W; }
        if (p.x < -10) p.x = W + 10;
        if (p.x > W + 10) p.x = -10;
      });
      animRef.current = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener('resize', resize);
    };
  }, []);

  // ─── POST to Express backend ───────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const res = await fetch("https://admin.nutrius.co/api/waitlist", {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ email }),
      });
      const data = await res.json();

      if (!res.ok) {
        setError(res.status === 409
          ? "You're already on the list! We'll be in touch."
          : data.message || 'Something went wrong. Please try again.'
        );
        return;
      }

      setSubmitted(true);
    } catch {
      setError('Unable to connect. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  // ──────────────────────────────────────────────────────────────────────────

  return (
    <div style={{ background: 'var(--color-background, #f9f6f0)' }}>

      {/* Navbar — no auth links during coming soon */}
      <nav className="bg-surface border-b border-text-tertiary/20 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-horizontal">
          <div className="flex items-center justify-between h-16">
            <span className="text-title-3 font-rounded text-primary">Nutrius</span>
            <button
              onClick={() => navigate('/login')}
              className="inline-flex items-center gap-sm text-caption font-semibold text-text-secondary hover:text-primary transition-colors"
            >
              <LogIn size={15} />
              Beta Access
            </button>
          </div>
        </div>
      </nav>

      {/* ── HERO ── */}
      <div className="relative overflow-hidden" style={{ minHeight: '92vh' }}>

        {/* Animated canvas layer */}
        <canvas
          ref={canvasRef}
          className="absolute inset-0 w-full h-full pointer-events-none"
          style={{ zIndex: 0 }}
        />

        {/* Floating orbs with parallax */}
        <Orb style={{
          width: 640, height: 640,
          top: -120, right: -160,
          transform: `translateY(${scrollY * 0.18}px)`,
          transition: 'transform 0.1s linear',
          zIndex: 0,
        }} />
        <Orb style={{
          width: 420, height: 420,
          bottom: 40, left: -100,
          transform: `translateY(${scrollY * -0.12}px)`,
          transition: 'transform 0.1s linear',
          zIndex: 0,
        }} />
        <Orb style={{
          width: 260, height: 260,
          top: '35%', left: '42%',
          transform: `translateY(${scrollY * 0.08}px)`,
          transition: 'transform 0.1s linear',
          zIndex: 0,
        }} />

        {/* Mesh gradient overlay */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `
              radial-gradient(ellipse 70% 60% at 80% 20%, rgba(45,80,22,0.07) 0%, transparent 70%),
              radial-gradient(ellipse 50% 50% at 10% 80%, rgba(45,80,22,0.05) 0%, transparent 60%),
              linear-gradient(160deg, rgba(45,80,22,0.04) 0%, transparent 50%)
            `,
            zIndex: 1,
          }}
        />

        {/* Hero content */}
        <div
          className="relative max-w-7xl mx-auto px-horizontal flex items-center"
          style={{ minHeight: '92vh', zIndex: 2 }}
        >
          <div className="max-w-2xl py-xxxl">

            {/* Badge */}
            <div
              className="inline-flex items-center gap-sm px-md py-xs rounded-full mb-xl text-caption font-semibold"
              style={{
                background: 'rgba(45,80,22,0.08)',
                color: 'var(--color-primary, #2D5016)',
                border: '1px solid rgba(45,80,22,0.15)',
                backdropFilter: 'blur(8px)',
              }}
            >
              <span
                className="w-1.5 h-1.5 rounded-full animate-pulse"
                style={{ background: 'var(--color-primary, #2D5016)' }}
              />
              Launching Soon
            </div>

            <h1
              className="font-rounded text-text-primary leading-tight mb-lg"
              style={{ fontSize: 'clamp(2.4rem, 5.5vw, 4rem)', letterSpacing: '-0.02em' }}
            >
              Shop healthy,
              <br />
              <span className="text-primary">filtered for your family</span>
            </h1>

            <p className="text-body text-text-secondary mb-xl leading-relaxed max-w-lg">
              Verified products and recipes filtered to your household's exact dietary needs.
              One platform, every meal.
            </p>

            {/* Email capture — original layout */}
            {!submitted ? (
              <div>
                <div className="flex gap-sm flex-wrap max-w-lg">
                  <input
                    type="email"
                    value={email}
                    onChange={e => { setEmail(e.target.value); setError(''); }}
                    onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                    placeholder="Enter your email for early access"
                    disabled={loading}
                    className="flex-1 min-w-[200px] px-lg py-md text-body focus:outline-none"
                    style={{
                      background: 'rgba(255,255,255,0.85)',
                      border: '1px solid rgba(45,80,22,0.2)',
                      borderRadius: '0.75rem',
                      backdropFilter: 'blur(12px)',
                      color: 'var(--color-text-primary)',
                    }}
                    onFocus={e => e.target.style.borderColor = 'rgba(45,80,22,0.6)'}
                    onBlur={e => e.target.style.borderColor = 'rgba(45,80,22,0.2)'}
                  />
                  <button
                    onClick={handleSubmit}
                    disabled={loading}
                    className="flex items-center gap-sm px-xl py-md text-white text-callout font-semibold flex-shrink-0 transition-all"
                    style={{
                      background: loading ? 'rgba(45,80,22,0.5)' : 'var(--color-primary, #2D5016)',
                      borderRadius: '0.75rem',
                      boxShadow: '0 4px 20px rgba(45,80,22,0.25)',
                      cursor: loading ? 'not-allowed' : 'pointer',
                    }}
                    onMouseEnter={e => { if (!loading) { e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.transform = 'translateY(-1px)'; }}}
                    onMouseLeave={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'translateY(0)'; }}
                  >
                    {loading ? 'Joining…' : <> Notify Me <ArrowRight size={16} /> </>}
                  </button>
                </div>
                {error && <p className="text-xs text-error mt-sm">{error}</p>}
                <p className="text-caption text-text-tertiary mt-sm">No spam. Unsubscribe anytime.</p>
              </div>
            ) : (
              <div
                className="inline-flex items-center gap-md p-lg"
                style={{
                  background: 'rgba(45,80,22,0.07)',
                  border: '1px solid rgba(45,80,22,0.18)',
                  borderRadius: '0.75rem',
                  backdropFilter: 'blur(8px)',
                }}
              >
                <CheckCircle size={20} className="text-primary flex-shrink-0" />
                <div>
                  <p className="text-callout font-semibold text-primary">You're on the list</p>
                  <p className="text-caption text-text-secondary">We'll reach out to {email} when we launch.</p>
                </div>
              </div>
            )}

            {/* Dietary pill row */}
            <div className="flex flex-wrap gap-sm mt-xl">
              {DIETARY_SHORTCUTS.map(({ label, icon: Icon }) => (
                <div
                  key={label}
                  className="flex items-center gap-xs px-md py-xs text-caption font-semibold"
                  style={{
                    background: 'rgba(255,255,255,0.7)',
                    border: '1px solid rgba(45,80,22,0.12)',
                    borderRadius: '999px',
                    backdropFilter: 'blur(8px)',
                    color: 'var(--color-primary)',
                  }}
                >
                  <Icon size={13} />
                  {label}
                </div>
              ))}
            </div>

            {/* Beta bypass */}
            <div
              className="flex items-center justify-between gap-lg mt-xxl p-lg"
              style={{
                background: 'rgba(45,80,22,0.05)',
                border: '1px solid rgba(45,80,22,0.14)',
                borderRadius: '0.75rem',
                backdropFilter: 'blur(8px)',
                maxWidth: '32rem',
              }}
            >
              <div className="flex items-center gap-sm">
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: 'rgba(45,80,22,0.1)' }}
                >
                  <Apple size={16} style={{ color: 'var(--color-primary)' }} />
                </div>
                <div>
                  <p className="text-callout font-semibold" style={{ color: 'var(--color-primary)' }}>
                    Already a beta tester?
                  </p>
                  <p className="text-caption" style={{ color: 'var(--color-text-secondary)' }}>
                    Sign in to access the full platform.
                  </p>
                </div>
              </div>
              <button
                onClick={() => navigate('/login')}
                className="flex items-center gap-xs px-lg py-sm text-caption font-semibold flex-shrink-0 transition-all hover:opacity-90"
                style={{
                  background: 'var(--color-primary)',
                  color: '#fff',
                  borderRadius: '999px',
                  boxShadow: '0 2px 12px rgba(45,80,22,0.2)',
                }}
              >
                Sign In <ArrowRight size={13} />
              </button>
            </div>

          </div>
        </div>

        {/* Bottom fade into content */}
        <div
          className="absolute bottom-0 left-0 right-0 h-24 pointer-events-none"
          style={{
            background: 'linear-gradient(to bottom, transparent, var(--color-background, #f9f6f0))',
            zIndex: 3,
          }}
        />
      </div>

      {/* ── BELOW FOLD ── */}
      <div className="max-w-7xl mx-auto px-horizontal space-y-xxxl pb-xxxl">

        {/* Trust bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-md">
          {[
            { icon: Shield,      label: 'Verified Products', value: 'Coming'        },
            { icon: BookOpen,    label: 'Community Recipes',  value: 'Coming'        },
            { icon: Users,       label: 'Dietary Filters',    value: '12'            },
            { icon: CheckCircle, label: 'Human Verified',     value: 'Every Product' },
          ].map(({ icon: Icon, label, value }) => (
            <div
              key={label}
              className="flex items-center gap-md p-md bg-surface rounded-large"
              style={{ border: '1px solid rgba(var(--color-text-tertiary),0.1)' }}
            >
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon size={18} className="text-primary" />
              </div>
              <div>
                <p className="text-callout font-bold text-text-primary">{value}</p>
                <p className="text-caption text-text-secondary">{label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Features */}
        <section>
          <div className="mb-lg">
            <h2 className="text-title-1 font-rounded text-primary">What's Coming</h2>
            <p className="text-body text-text-secondary mt-xs">Everything you need to manage dietary needs at home</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-md">
            {FEATURES.map(({ icon: Icon, label, desc }) => (
              <div
                key={label}
                className="flex items-start gap-lg p-lg bg-surface rounded-large"
                style={{ border: '1px solid rgba(var(--color-text-tertiary),0.1)' }}
              >
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Icon size={18} className="text-primary" />
                </div>
                <div>
                  <p className="text-callout font-semibold text-text-primary mb-xs">{label}</p>
                  <p className="text-caption text-text-secondary">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Verified callout */}
        <section
          className="p-xl rounded-large"
          style={{ border: '1px solid rgba(var(--color-text-tertiary),0.1)', background: 'rgba(var(--color-primary-rgb),0.03)' }}
        >
          <div className="flex items-start gap-xl">
            <div className="flex-1">
              <div className="flex items-center gap-sm mb-md">
                <Shield size={20} className="text-primary" />
                <h2 className="text-title-2 font-bold text-text-primary">Human Verified Products</h2>
              </div>
              <p className="text-body text-text-secondary mb-lg max-w-xl">
                Every product in the Nutrius catalog is reviewed by a real person before receiving a verified badge.
                For households managing serious dietary conditions, that distinction matters.
              </p>
              <div
                className="inline-flex items-center gap-sm px-lg py-md rounded-large text-callout font-medium text-text-tertiary"
                style={{ border: '1px solid rgba(var(--color-text-tertiary),0.2)' }}
              >
                Catalog launching soon
              </div>
            </div>
            <div className="hidden md:flex flex-col gap-sm">
              {['Allergen accurate', 'Dietary tag verified', 'Ingredient reviewed', 'Community trusted'].map(item => (
                <div key={item} className="flex items-center gap-sm">
                  <CheckCircle size={16} className="text-primary flex-shrink-0" />
                  <span className="text-callout text-text-secondary">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* App CTA */}
        <section
          className="p-xl rounded-large text-center"
          style={{ border: '1px solid rgba(var(--color-text-tertiary),0.1)', background: 'rgba(var(--color-primary-rgb),0.03)' }}
        >
          <div
            className="inline-flex items-center gap-sm px-md py-xs rounded-full mb-md text-caption font-semibold"
            style={{ background: 'rgba(45,80,22,0.08)', color: 'var(--color-primary)', border: '1px solid rgba(45,80,22,0.15)' }}
          >
            <Sparkles size={13} />
            Beta Programme
          </div>
          <h2 className="text-title-1 font-rounded text-primary mb-sm">Take Nutrius With You</h2>
          <p className="text-body text-text-secondary mb-lg max-w-md mx-auto">
            Join our iOS beta on TestFlight. Scan products in store, manage grocery lists on the go, and cook with confidence.
          </p>
          <div className="flex items-center justify-center gap-md flex-wrap">
            <button
              onClick={() => navigate('/login')}
              className="inline-flex items-center gap-sm px-xl py-md text-white text-callout font-semibold hover:opacity-90 transition-all"
              style={{ background: 'var(--color-primary)', borderRadius: '0.75rem', boxShadow: '0 4px 20px rgba(45,80,22,0.2)' }}
            >
              <LogIn size={18} /> Sign In to Access
            </button>
            <a
              href="/beta"
              className="inline-flex items-center gap-sm px-xl py-md text-callout font-semibold hover:opacity-80 transition-all"
              style={{ border: '1px solid rgba(45,80,22,0.25)', borderRadius: '0.75rem', color: 'var(--color-primary)' }}
            >
              <Apple size={18} /> Join the Beta
            </a>
          </div>
          <p className="text-caption text-text-tertiary mt-md">Free via Apple TestFlight · Android coming soon</p>
        </section>

      </div>

      {/* Footer */}
      <footer className="bg-surface border-t border-text-tertiary/20">
        <div className="max-w-7xl mx-auto px-horizontal py-lg">
          <div className="flex flex-col md:flex-row justify-between items-center gap-md">
            <div className="flex items-center gap-lg">
              <span className="text-callout font-rounded text-primary">Nutrius</span>
              <span className="text-caption text-text-tertiary">A product of HealthWealth LLC</span>
            </div>
            <div className="flex items-center gap-lg">
              <a href="mailto:hello@nutrius.co" className="text-caption text-text-secondary hover:text-primary transition-quick">Contact</a>
              <a href="/beta" className="text-caption text-text-secondary hover:text-primary transition-quick">iOS Beta</a>
              <a href="mailto:privacy@nutrius.co" className="text-caption text-text-secondary hover:text-primary transition-quick">Privacy</a>
            </div>
            <p className="text-caption text-text-secondary">© {new Date().getFullYear()} HealthWealth LLC.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ComingSoonView;